import { Button } from "@crew/ui/components/button";
import { StatusDot } from "@crew/ui/components/status";
import { TextLink } from "@crew/ui/components/typography";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { chiefOf } from "../chats/authors";
import { useRetryComputer } from "./retry";
import { canRetryComputer, computerView, needsAiSettings } from "./status";

function RetryComputer() {
  const retry = useRetryComputer();
  return (
    <div className="flex items-center gap-3">
      <Button onClick={() => retry.mutate()} disabled={retry.isPending}>
        {retry.isPending ? "Retrying…" : "Retry"}
      </Button>
      {retry.error ? <span className="text-sm text-brick">{errorHint(retry.error)}</span> : null}
    </div>
  );
}

/**
 * The computer's state at the top of Chats, live from the `computer` event,
 * only when there's something to do (Retry, Settings → AI). Otherwise the
 * sidebar's status (the top bar's dot on a phone) says it.
 */
export function ComputerCard({ me }: { me: Me }) {
  if (!hasComputerAction(me)) return null;
  const view = computerView(me.computer);
  return (
    <section
      aria-label="Your computer"
      data-computer-status={me.computer?.status ?? "none"}
      className="flex flex-col gap-3 rounded-well bg-muted p-5"
    >
      <div className="flex items-center gap-2">
        <StatusDot tone={view.tone} />
        <p role="status" className="text-lead text-foreground">
          {view.line}
        </p>
      </div>
      <p className="text-body text-muted-foreground">
        {chiefOf(me).name} works on your workspace's own computer.
      </p>
      <ComputerAction me={me} />
    </section>
  );
}

// Settings → AI is the owner's (slice 6a §6.5): a member's composer says who can connect it.
const hasComputerAction = (me: Me) =>
  canRetryComputer(me) || (needsAiSettings(me.computer) && me.user.role === "owner");

/** Retry after an error; the way to Settings → AI when the AI isn't connected. */
function ComputerAction({ me }: { me: Me }) {
  if (canRetryComputer(me)) return <RetryComputer />;
  if (!needsAiSettings(me.computer)) return null;
  return (
    <TextLink href="/settings" className="self-start">
      Settings → AI
    </TextLink>
  );
}
